import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, ExternalLink } from 'lucide-react';
const Footer = () => {
  const {
    t
  } = useTranslation();
  return <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <div className="mb-6 px-[28px]">
              <Link to="/" className="inline-block">
                <div className="h-32 w-32 rounded-full border-4 border-blue-500 bg-white p-2 flex items-center justify-center overflow-hidden">
                  <img alt="oFair" src="/lovable-uploads/0f6f775f-453e-49b4-b0df-2ccfee430b8f.png" width={104} height={104} loading="lazy" className="h-full w-full object-contain" />
                </div>
              </Link>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-0.5 bg-emerald-400 rounded-full"></span>
              {t('footer.quickNav')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link to="/search" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.searchProfessionals')}
                </Link>
              </li>
              <li>
                <Link to="/articles" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.tipsAndArticles')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.about')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Important Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-0.5 bg-emerald-400 rounded-full"></span>
              {t('footer.importantInfo')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.termsOfService')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.faq')}
                </Link>
              </li>
              <li>
                <Link to="/accessibility" className="text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-0.5 bg-emerald-400 transition-all duration-200"></span>
                  {t('footer.accessibility')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white flex items-center gap-2">
              <span className="w-8 h-0.5 bg-emerald-400 rounded-full"></span>
              {t('footer.contactUs')}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-slate-300 text-sm">
                <div className="w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-emerald-400" />
                </div>
                <a className="hover:text-emerald-400 transition-colors" href="mailto:contact@ofair.co.il">
                  contact@ofair.co.il
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-300 text-sm">
                <div className="w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-emerald-400" />
                </div>
                <a href="tel:050-5577565" className="hover:text-emerald-400 transition-colors">
                  050-5577565
                </a>
              </li>
            </ul>
            
            {/* CTA Button */}
            <div className="mt-8">
              <a href="https://biz.ofair.co.il" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 text-sm bg-yellow-600 text-slate-900 font-semibold">
                {t('footer.professionalJoin')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700/50">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              {t('footer.copyright', {
              year: new Date().getFullYear()
            })}
            </p>
            <div className="flex items-center gap-2 text-slate-500 text-xs">
              <span className="text-purple-700">By WEBSudio.co.il</span>
              <span className="text-red-400">❤</span>
              <span className="text-violet-700">MADE WITH</span>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;